import getItemViewAppliedLoyaltyUpgrade from 'checkout/lib/utils/loyalty/getItemViewAppliedLoyaltyUpgrade'
import { excludeNullOrUndefined, getBreakdownTitle } from 'checkout/utils'
import { DMY_CASUAL_FORMAT, ISO_DATE_FORMAT } from 'constants/dateFormats'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK, OFFER_TYPE_HOTEL, OFFER_TYPE_TOUR, OFFER_TYPE_VILLA } from 'constants/offer'
import { RESERVATION_TYPE_BOOK_LATER } from 'constants/reservation'
import { getAgentHubCommissionItemKey } from 'lib/agentHub/agentHubUtils'
import { groupBy, sum } from 'lib/array/arrayUtils'
import { isAccommodationItem, isBedbankItem, isInstantBookingLEHotelItem, isLEHotelItem } from 'lib/checkout/checkoutUtils'
import { formatOccupantsShort, sumUpOccupancies } from 'lib/offer/occupancyUtils'
import { isBedbankType } from 'lib/offer/offerTypes'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { capitalise } from 'lib/string/stringUtils'
import moment from 'moment'
import { createSelector } from 'reselect'
import { isPostPurchaseAncillaryPayment } from '../payment/isPostPurchaseAncillaryPayment'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { getBundledDates } from 'checkout/lib/utils/flights/getBundledDates'

/**
 * @remarks
 * We need this selector for a few functions within this file but cannot use getAccommodationItems from /checkout/selectors/view/accommodation.ts
 * because this would create a circular import dependency. This is because accommodation.ts requires getHotelBreakdownView from this file.
 * We have the same problem in deposit.ts, so be sure to update all 3 functions. Looking to implement a better, long term solution soon.
 */
const getCartAccommodationItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.AccommodationItem> => items.filter(isAccommodationItem),
  reselectShallowEqual,
)

const getOrderAccommodationItems = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (existingOrder): Array<App.Checkout.AccommodationItem> => {
    return existingOrder?.items.reduce<Array<App.Checkout.AccommodationItem>>((acc, item) => {
      if ('roomRateId' in item.package && item.reservationType === 'instant_booking' && item.reservation) {
        acc.push({
          itemId: item.id,
          transactionKey: item.transactionKey,
          offerId: item.offer.id,
          duration: item.duration || 0,
          itemType: 'hotel',
          packageId: item.package.id,
          roomRateId: item.package.roomRateId,
          reservationType: item.reservationType,
          checkIn: item.reservation.startDate,
          checkOut: item.reservation.endDate,
          occupancy: {
            adults: item.reservation.adults,
            teenagers: item.reservation.teenagers,
            children: item.reservation.children,
            infants: item.reservation.infants,
            childrenAge: item.reservation.childrenAge,
          },
        })
      }
      return acc
    }, []).concat(existingOrder.bedbankItems.reduce<Array<App.Checkout.BedbankHotelItem>>((acc, item) => {
      return acc.concat(item.rooms.map(room => ({
        itemId: item.id,
        sessionId: '',
        roomId: item.roomTypeId,
        checkIn: moment(item.checkIn).format(ISO_DATE_FORMAT),
        checkOut: moment(item.checkIn).format(ISO_DATE_FORMAT),
        roomRateId: item.roomRateId,
        isFlightBundle: room.isFlightBundle,
        bedGroupId: '',
        transactionKey: item.transactionKey,
        offerId: item.offer.id,
        duration: item.duration,
        newPrice: item.total,
        itemType: 'bedbankHotel',
        occupancy: room.occupancy,
      })))
    }, [])) || []
  },
)

export const getAccommodationItems = createSelector(
  (state: App.State) => getCartAccommodationItems(state),
  (state: App.State) => getOrderAccommodationItems(state),
  (state: App.State) => isPostPurchaseAncillaryPayment(state),
  (accommodationItemsFromCart, accommodationItemsFromOrder, isAncillaryPayment): Array<App.Checkout.AccommodationItem> => {
    return isAncillaryPayment ? accommodationItemsFromOrder : accommodationItemsFromCart
  },
)

function getCancellationPolicyBreakdown(
  item: App.Checkout.LEAccommodationItemView | App.Checkout.BedbankAccommodationItemView,
  offerView: App.Checkout.LEAccommodationOfferView | App.Checkout.BedbankAccommodationOfferView,
) {
  const { offerType } = offerView

  switch (offerType) {
    case OFFER_TYPE_HOTEL: {
      const pkg = offerView.offer?.packages.find(pkg => pkg.uniqueKey === item.uniqueKey)
      return {
        cancellationPolicyDetails: pkg?.roomRate?.cancellationPolicy,
        checkInDate: moment(offerView.startDate),
        checkOutDate: moment(offerView.startDate).clone().add(offerView.duration, 'days'),
        occupants: item.occupancy,
      }
    }
    case OFFER_TYPE_BED_BANK:
      return {
        propertyTimezone: offerView.propertyTimezone,
        rate: item.rate,
        showNonCancellation: !(item.rate?.refundable),
      }
    case OFFER_TYPE_VILLA:
    case OFFER_TYPE_ALWAYS_ON: {
      const pkg = offerView.offer?.packages.find(pkg => pkg.uniqueKey === item.uniqueKey)
      return {
        cancellationPolicyDetails: pkg?.roomRate?.cancellationPolicy,
        ignoreDynamicCancellationPolicy: pkg?.ignoreDynamicCancellationPolicy ?? false,
        useDynamicCancellationPolicies: offerView.offer?.property?.useDynamicCancellationPolicies ?? false,
        timezoneOffset: offerView.offer?.property?.timezoneOffset,
        checkInDate: moment(offerView.startDate),
        checkOutDate: moment(offerView.startDate).clone().add(offerView.duration, 'days'),
        occupants: item.occupancy,
        offerId: offerView.offerId,
        uniqueKey: item.uniqueKey,
      }
    }
    default: undefined
  }
}

function getHotelBreakdownItems(
  itemViews: Array<App.Checkout.LEAccommodationItemView> | Array<App.Checkout.BedbankAccommodationItemView>,
  offerView: App.Checkout.LEAccommodationOfferView | App.Checkout.BedbankAccommodationOfferView,
  hidePrice: boolean,
): Array<App.Checkout.AccommodationItemBreakdownView> {
  const intl = getCurrentIntl()
  return itemViews.map<App.Checkout.AccommodationItemBreakdownView>(
    (item, idx: number) => {
      const result: App.Checkout.AccommodationItemBreakdownView = {
        title: item.primaryTitle,
        additionalInfo: [
          { text: formatOccupantsShort(item.occupancy) },
        ],
        additionalElements: [],
        itemType: 'accommodation',
        price: hidePrice ? 0 : (item.totals.price ?? 0) + (item.totals.surcharge ?? 0) - item.totals.taxesAndFees,
        memberPrice: (hidePrice || item.totals.memberPrice === 0) ? 0 : (item.totals.memberPrice ?? 0) + (item.totals.surcharge ?? 0) - item.totals.taxesAndFees,
        soldOut: item.soldOut,
        taxesAndFees: item.totals.taxesAndFees,
        offerId: item.item.offerId ?? offerView.offerId,
        itemId: item.item.itemId,
        cancellationPolicy: getCancellationPolicyBreakdown(item, offerView),
        transactionKey: item.item.transactionKey,
      }

      const loyaltyUpgradeApplied = getItemViewAppliedLoyaltyUpgrade(item)
      if (loyaltyUpgradeApplied) {
        result.additionalInfo.unshift({
          text: loyaltyUpgradeApplied.originPrimaryTitle,
          strikethrough: true,
        })
        result.title = loyaltyUpgradeApplied.targetPrimaryTitle
      }

      if (item.kind === 'le' && item.totals.extraGuestSurcharge && item.totals.extraGuestSurcharge > 0) {
        const extraGuestSurcharge = item.totals?.otherFees?.extraGuestSurcharge ?? 0
        result.additionalElements.push({
          id: `extra_guest_surcharge_${idx}`,
          left: {
            type: 'text',
            value: extraGuestSurcharge > 0
              ? intl.formatMessage({
                  id: 't/3nWUqyLG',
                  defaultMessage: 'Extra guest surcharge (payable at property)',
                  description: 'Label for extra guest surcharge that is payable at the property',
                })
              : intl.formatMessage({
                  id: 'N6XAQeOzKA',
                  defaultMessage: 'Extra guest surcharge',
                  description: 'Label for extra guest surcharge',
                }),
          },
          right: {
            type: 'price',
            value: item.totals.extraGuestSurcharge,
          },
        })
      }

      return result
    })
}

export const getLeHotelItems = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): Array<App.Checkout.LEHotelItem> => items.filter(isLEHotelItem),
  reselectShallowEqual,
)

export const getInstantBookingLEHotelItems = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): Array<App.Checkout.InstantBookingLEHotelItem> => items.filter(isInstantBookingLEHotelItem),
  reselectShallowEqual,
)

export const getBedbankItems = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (items): Array<App.Checkout.BedbankHotelItem> => items.filter(isBedbankItem),
  reselectShallowEqual,
)

function getBNBLBreakdownItems(
  itemViews: Array<App.Checkout.LEAccommodationItemView | App.Checkout.BedbankAccommodationItemView>,
  hidePrice: boolean,
): Array<App.Checkout.AccommodationItemBreakdownView> {
  const intl = getCurrentIntl()
  const itemGroups = groupBy(itemViews, item => item.primaryTitle)
  return Array.from(itemGroups.entries()).map<App.Checkout.AccommodationItemBreakdownView>(
    ([mainLabel, groupedItems]) => {
      return {
        itemId: groupedItems[0].item.itemId,
        title: mainLabel,
        itemType: 'accommodation',
        offerId: groupedItems[0].item.offerId, // double check
        additionalInfo: [{
          text: intl.formatMessage(
            {
              id: 'ZWVycL/aXR',
              defaultMessage: '{count, plural, one {# package} other {# packages}}',
              description: 'Package count in BNBL hotel breakdown',
            },
            { count: groupedItems.length },
          ),
        }],
        additionalElements: [],
        price: hidePrice ? undefined : sum(groupedItems, item => (item.totals.price ?? 0) + (item.totals.surcharge ?? 0) - item.totals.taxesAndFees),
        memberPrice: hidePrice ? undefined : sum(groupedItems, item => item.totals.memberPrice > 0 ? (item.totals.memberPrice ?? 0) + (item.totals.surcharge ?? 0) - item.totals.taxesAndFees : 0),
        reservationType: RESERVATION_TYPE_BOOK_LATER,
      }
    })
}

export function getHotelBreakdownView(
  offerView: App.Checkout.LEAccommodationOfferView | App.Checkout.BedbankAccommodationOfferView,
  flightTotal: App.WithDataStatus<App.Checkout.ItemViewTotals>,
  hidePrice: boolean,
  hasLuxPlusOrSubscriptionInCart: boolean,
  commissionData?: App.AgentHubCommission,
  hideCruiseV1: boolean = true,
  flightItems?: Array<App.Checkout.FlightItem>,
  journeysById?: Record<string, App.AnyJourney>,
): App.Checkout.PriceBreakdownView {
  const intl = getCurrentIntl()
  const { offerType, durationLabel, startDate, endDate, itemViews, mainLabel, offer } = offerView
  const isCruiseV1 = offer?.type === OFFER_TYPE_TOUR && (offer)?.holidayTypes?.includes('Cruises')
  const isBedbank = isBedbankType(offerType)
  const isBNBL = offerView.reservationType == RESERVATION_TYPE_BOOK_LATER

  const guestCount = sumUpOccupancies(itemViews.map(itemView => itemView.occupancy).filter(excludeNullOrUndefined))
  const roomsAndGuestsLabel = [
    isCruiseV1 ? null : intl.formatMessage(
      {
        id: '8Q226QWk1C',
        defaultMessage: '{count, plural, one {# room} other {# rooms}}',
        description: 'Room count in hotel breakdown info',
      },
      { count: itemViews.length },
    ),
    guestCount ? intl.formatMessage(
      {
        id: 'iBSlIE7Q2I',
        defaultMessage: '{count, plural, one {# guest} other {# guests}}',
        description: 'Guest count in hotel breakdown info',
      },
      { count: guestCount },
    ) : null,
  ].filter(excludeNullOrUndefined).join(' · ')

  const items = isBNBL ?
      getBNBLBreakdownItems(itemViews, hidePrice) :
      getHotelBreakdownItems(itemViews, offerView, hidePrice)

  const hotelPrice = sum(items, item => item.price ?? 0)
  const hotelMemberPrice = sum(items, item => (item.memberPrice || item.price) ?? 0)

  let price = offerView.isBundled ? hotelPrice + (flightTotal.data.price ?? 0) : hotelPrice
  const memberPrice = offerView.isBundled ? hotelMemberPrice + (flightTotal.data.price ?? 0) : hotelMemberPrice

  const soldOut = items.some(item => item.soldOut)

  // Get flight dates when bundled
  let displayStartDate = startDate
  let displayEndDate = endDate
  if (offerView.isBundled && flightItems && journeysById) {
    const bundledDates = getBundledDates({
      isBundled: offerView.isBundled,
      startDate,
      endDate,
      itemViews,
      flightItems,
      journeysById,
    })

    if (bundledDates.flightDepartureDate && bundledDates.flightReturnDate) {
      displayStartDate = bundledDates.flightDepartureDate
      displayEndDate = bundledDates.flightReturnDate
    } else if (bundledDates.flightDepartureDate) {
      // Fallback to departure date if return journey not available
      displayStartDate = bundledDates.flightDepartureDate
    }
  }

  const additionalInfoText = (isBNBL || !displayStartDate || !displayEndDate) ?
      [`${durationLabel} (${roomsAndGuestsLabel})`] :
      [
      `${moment(displayStartDate).format(DMY_CASUAL_FORMAT)} - ${moment(displayEndDate).format(DMY_CASUAL_FORMAT)}`,
      `${durationLabel} (${roomsAndGuestsLabel})`,
      ]

  const displayPricingAsPerPerson = offerType == OFFER_TYPE_BED_BANK && offer?.displayPricingAsPerPerson
  let pricePerPerson
  if (displayPricingAsPerPerson) {
    const adultsCount = sumUpOccupancies(itemViews.map(itemView => itemView.occupancy).filter(excludeNullOrUndefined), new Set(['adults']))
    if (adultsCount > 1) {
      pricePerPerson = Math.round(price / adultsCount)
    }
  }

  let hasMobilePromotion = false
  if (isBedbank) {
    hasMobilePromotion = offerView.itemViews.some(x => x?.rate?.mobilePromotion)
  }

  // This is here because we can still sell member only offers to non members when spoofing
  if (offerView.offer?.luxPlus?.access === 'memberOnly' && hasLuxPlusOrSubscriptionInCart) {
    price = memberPrice || price
  }

  const totalCommissionDiscount = !commissionData ? 0 : sum(items, item => commissionData.itemsCommission[getAgentHubCommissionItemKey(item)]?.itemDiscount ?? 0)
  const commissionPercentage = Number((totalCommissionDiscount / price * 100).toFixed(2))

  let title = ''
  if (offerView.isBundled) {
    title = intl.formatMessage({
      id: 'yce+MyTLUF',
      defaultMessage: 'Hotel + Flights Package',
      description: 'Title for bundled hotel and flights package in checkout breakdown',
    })
  } else if (offerView.bundledWithTour) {
    title = capitalise(offerView.designation)
  } else {
    title = getBreakdownTitle(offerView)
  }

  return {
    title,
    description: mainLabel,
    additionalInfoText,
    items: isCruiseV1 && hideCruiseV1 ? [] : items,
    price: (hidePrice ? 0 : price),
    memberPrice: (hidePrice ? 0 : memberPrice),
    soldOut,
    displayPricingAsPerPerson,
    pricePerPerson,
    productType: offerView.offerType,
    hasMobilePromotion,
    ...(commissionData && {
      commission: { commissionPercentage, itemDiscount: totalCommissionDiscount },
    }),
    startDate: displayStartDate,
  }
}
